/*
 * Copyright (c) 2026 MTa Learning. All Rights Reserved.
 *
 * MTa Learning proprietary information: the enclosed materials contain proprietary information of MTa Learning and
 * shall not be disclosed in whole or in any part to any third party or used by any person for any purpose, without
 * written consent of MTa Learning. Duplication of any portion of these materials shall include this notice.
 */

/** The name of the first-party cookie that stores the latest Google Ads click. */
;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:mta-learning":true});}catch(e){}}();export const GCLID_COOKIE = 'mta_gclid'

/** How long a captured click stays valid, in milliseconds (90 days). */
export const GCLID_MAX_AGE = 90 * 24 * 60 * 60 * 1000

/** The shape of a Google click ID. Matches the validation in the VELA API. */
const GCLID_PATTERN = /^[A-Za-z0-9._~-]{1,512}$/

/** The public Zoho Forms hosts that may receive a click ID. */
const ZOHO_FORM_HOST_PATTERN = /^forms\.zohopublic\.(com|eu|in|com\.au|jp|ca|sa|com\.cn)$/

/** A captured click, as stored in the cookie. */
type Click = {
  /** The click ID from the landing URL. */
  value: string

  /** When the click stops being attributed, as a Unix timestamp in milliseconds. */
  expiresAt: number
}

/**
 * Checks whether a value has the shape of a Google click ID.
 *
 * @param value - the value to check
 */
export const isValidGclid = (value: unknown): value is string => {
  return typeof value === 'string' && GCLID_PATTERN.test(value)
}

/**
 * Checks whether a parsed cookie value is a usable click: a valid ID that has not expired, and whose expiry is no
 * further away than a fresh capture would set.
 *
 * @param click - the parsed cookie value
 */
const isUsableClick = (click: Click): boolean => {
  const now = Date.now()

  return (
    isValidGclid(click.value) &&
    Number.isFinite(click.expiresAt) &&
    click.expiresAt > now &&
    click.expiresAt <= now + GCLID_MAX_AGE
  )
}

/**
 * Reads the stored click from its cookie. Returns `undefined` when the cookie is missing, malformed or expired.
 */
const readClick = (): Click | undefined => {
  try {
    const cookie = document.cookie.split('; ').find((part) => part.startsWith(`${GCLID_COOKIE}=`))

    if (!cookie) {
      return undefined
    }

    const click: Click = JSON.parse(decodeURIComponent(cookie.slice(GCLID_COOKIE.length + 1)))

    if (!isUsableClick(click)) {
      return undefined
    }

    return click
  } catch {
    // A malformed or inaccessible cookie must not break the form.
    return undefined
  }
}

/**
 * Stores a click in its cookie, expiring at the same time as the click.
 *
 * @param click - the click to store
 */
const writeClick = (click: Click): void => {
  const value = encodeURIComponent(JSON.stringify(click))
  const attributes = [`Expires=${new Date(click.expiresAt).toUTCString()}`, 'Path=/', 'SameSite=Lax']

  if (window.location.protocol === 'https:') {
    attributes.push('Secure')
  }

  try {
    document.cookie = [`${GCLID_COOKIE}=${value}`, ...attributes].join('; ')
  } catch {
    // Storage can be blocked. Submission still works without attribution.
  }
}

/**
 * Returns the stored click ID, or an empty string when there is none.
 */
export const readGclid = (): string => {
  if (typeof window === 'undefined') {
    return ''
  }

  return readClick()?.value ?? ''
}

/**
 * Stores the click ID from the URL, if there is a valid one, and returns the click ID now stored. A new click replaces
 * the previous one. Visits without a click, or with the same click, never extend the original expiry.
 *
 * @param search - the URL query string to read the click ID from
 */
export const captureGclid = (search: string = window.location.search): string => {
  const candidate = new URLSearchParams(search).get('gclid')

  if (isValidGclid(candidate) && candidate !== readClick()?.value) {
    writeClick({ value: candidate, expiresAt: Date.now() + GCLID_MAX_AGE })
  }

  return readGclid()
}

/**
 * Adds the click ID to a Zoho form URL. Zoho Forms reads `gclid` from its own URL, because an embedded form cannot
 * read the parent page. Any URL that is not an HTTPS Zoho Forms URL is returned unchanged, so the ID is never sent to
 * another host.
 *
 * @param formUrl - the Zoho form URL
 * @param gclid - the click ID, or an empty string to remove any `gclid` already in the URL
 */
export const zohoFormUrl = (formUrl: string, gclid: string): string => {
  let url: URL

  try {
    url = new URL(formUrl)
  } catch {
    return formUrl
  }

  if (url.protocol !== 'https:' || !ZOHO_FORM_HOST_PATTERN.test(url.hostname)) {
    return formUrl
  }

  url.searchParams.delete('gclid')

  if (isValidGclid(gclid)) {
    url.searchParams.set('gclid', gclid)
  }

  return url.toString()
}
